import {
  EMAIL_REGEX_PATTERN,
  PHONE_REGEX_PATTERN,
  LOCALE_REGEX_PATTERN,
} from '~/constants'
import { useRuntimeConfig } from '#app'
import { useLocale, useRouteHelpers } from '~/composables'

export function useCMSLink() {
  const locale = useLocale()
  const config = useRuntimeConfig()
  const { getLocalizedRoute } = useRouteHelpers()

  function isURLMail(url: string): boolean {
    return EMAIL_REGEX_PATTERN.test(url)
  }
  function isURLPhone(url: string): boolean {
    return PHONE_REGEX_PATTERN.test(url)
  }

  function isURLAbsolute(url: string): boolean {
    return url.startsWith('https://') || url.startsWith('www')
  }

  function toTelOrMail(u: string): string | null {
    if (u.startsWith('mailto:') || u.startsWith('tel:')) {
      return u
    }
    if (isURLMail(u)) {
      return `mailto:${u}`
    }
    if (isURLPhone(u)) {
      return `tel:${u}`
    }
    return null
  }

  function normalizeAbsoluteUrl(u: string): string {
    try {
      const parsed = new URL(u)
      if (parsed.host !== config.public.baseUrl) {
        return u
      }

      const parts = parsed.pathname.split('/').filter(Boolean)
      const first = parts[0]

      if (!LOCALE_REGEX_PATTERN.test(first)) {
        parsed.pathname = `/${locale.value}${parsed.pathname}`
      }
      return parsed.toString()
    } catch (e) {
      console.error(e)
      return u
    }
  }

  function normalizeRelativeUrl(u: string): string {
    const special = toTelOrMail(u)
    if (special) {
      return special
    }

    const path = u.startsWith('/') ? u : `/${u}`
    return getLocalizedRoute(path).toString()
  }

  function normalizeURL(url: string): string {
    if (!url) {
      return ''
    }
    const trimmed = url.trim()

    if (isURLAbsolute(trimmed)) {
      return normalizeAbsoluteUrl(trimmed)
    }
    return normalizeRelativeUrl(trimmed)
  }

  function parseURL(url: string) {
    if (!url) {
      return {
        isMail: null,
        isPhone: null,
        isAbsolute: null,
        normalized: '',
      }
    }

    return {
      isMail: isURLMail(url),
      isPhone: isURLPhone(url),
      isAbsolute: isURLAbsolute(url),
      normalized: normalizeURL(url),
    }
  }

  return {
    parseURL,
  }
}
